import React from 'react';
import { CheckCircle, XCircle, Copy, DollarSign } from 'lucide-react';
import type { GoDaddyAvailabilityToolResult, ToolResultRendererProps } from '../../../types/toolResults';
import Tooltip from '../../common/Tooltip';

/**
 * Renderer for GoDaddy domain availability check results.
 *
 * Displays availability status, pricing, and provides copy-to-clipboard functionality.
 * Uses a table/grid layout with color-coded availability indicators.
 */
const DomainAvailabilityRenderer: React.FC<ToolResultRendererProps<GoDaddyAvailabilityToolResult>> = ({
  result,
  onCopy,
  className = ''
}) => {
  const { available, unavailable, total_checked } = result;

  const handleCopy = (text: string) => {
    if (onCopy) {
      onCopy(text);
    } else {
      navigator.clipboard.writeText(text);
    }
  };

  const formatPrice = (priceInCents?: number, currency: string = 'USD'): string => {
    if (!priceInCents) return 'N/A';
    const price = priceInCents / 100;
    return `$${price.toFixed(2)} ${currency}`;
  };

  // Create combined list with availability derived from array membership
  // This ensures correct display even if the 'available' field is missing from individual items
  // (which can happen when agent output models don't include the field)
  const allDomains = [
    ...available.map(d => ({ ...d, isAvailable: true })),
    ...unavailable.map(d => ({ ...d, isAvailable: false }))
  ];

  if (allDomains.length === 0) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <p className="text-gray-500 dark:text-gray-400">No domains checked</p>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Summary Card */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Domain Availability Check</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {total_checked} domain{total_checked !== 1 ? 's' : ''} checked
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">{available.length}</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Available</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">{unavailable.length}</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Taken</p>
            </div>
          </div>
        </div>
      </div>

      {/* Results Table */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Domain
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Price
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-cap-dark divide-y divide-gray-200 dark:divide-gray-700">
            {allDomains.map((domainResult, index) => (
              <tr
                key={`${domainResult.domain}-${index}`}
                className={domainResult.isAvailable
                  ? 'bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30'
                  : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800'}
              >
                {/* Status Column */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {domainResult.isAvailable ? (
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                        <span className="text-sm font-medium text-green-800 dark:text-green-300">Available</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                        <span className="text-sm font-medium text-red-800 dark:text-red-300">Taken</span>
                      </div>
                    )}
                  </div>
                </td>

                {/* Domain Column */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-semibold text-gray-900 dark:text-warm-white">{domainResult.domain}</div>
                  {domainResult.error && (
                    <div className="text-xs text-red-600 dark:text-red-400 mt-1">{domainResult.error}</div>
                  )}
                </td>

                {/* Price Column */}
                <td className="px-6 py-4 whitespace-nowrap">
                  {domainResult.isAvailable && domainResult.price ? (
                    <div className="flex items-center space-x-1">
                      <DollarSign className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900 dark:text-warm-white">
                        {formatPrice(domainResult.price, domainResult.currency)}
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">—</span>
                  )}
                </td>

                {/* Actions Column */}
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <Tooltip content="Copy domain name">
                    <button
                      onClick={() => handleCopy(domainResult.domain)}
                      className="inline-flex items-center space-x-1 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors"
                    >
                      <Copy className="w-3 h-3" />
                      <span>Copy</span>
                    </button>
                  </Tooltip>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Copy All Available Button */}
      {available.length > 0 && (
        <div className="flex justify-end">
          <button
            onClick={() => {
              const domains = available.map(d => d.domain).join('\n');
              handleCopy(domains);
            }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/40 rounded-md transition-colors border border-green-300 dark:border-green-800"
          >
            <Copy className="w-4 h-4" />
            <span>Copy All Available Domains</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default DomainAvailabilityRenderer;
